/**
 * AnalysisDetails Component
 * T026: Displays AI-generated analysis with reasons, category, and tactics
 * Shows detailed breakdown of why a message is classified as suspicious/high_risk/critical
 */

import { useTranslation } from 'react-i18next';

interface ReasonItem {
  labelKey: string;
  params?: Record<string, string>;
}

interface AnalysisDetailsProps {
  reasonItems: ReasonItem[];
  mappedScamTypeKey?: string;
  mappedTacticKeys?: string[];
}

export function AnalysisDetails({
  reasonItems,
  mappedScamTypeKey,
  mappedTacticKeys,
}: AnalysisDetailsProps) {
  const { t } = useTranslation();

  if (!reasonItems || reasonItems.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
      <h3 className="mb-3 text-lg font-semibold text-orange-900">
        {t('sms.analysis.title', 'Analysis Details')}
      </h3>

      {/* Reason Items - Why this message is suspicious */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-orange-800">
          {t('sms.analysis.reasons', 'Detected Threats:')}
        </h4>
        <ul className="list-inside list-disc space-y-1 text-sm text-orange-900">
          {reasonItems.map((item, index) => (
            <li key={index}>
              {t(item.labelKey, { ...item.params, defaultValue: item.labelKey })}
            </li>
          ))}
        </ul>
      </div>

      {/* T026A: Scam Category */}
      {mappedScamTypeKey && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-orange-800">
            {t('sms.analysis.category', 'Scam Category:')}
          </h4>
          <p className="mt-1 text-sm text-orange-900">
            {t(`sms.category.${mappedScamTypeKey}.title`, mappedScamTypeKey)}
          </p>
        </div>
      )}

      {/* T026A: Tactics Badges */}
      {mappedTacticKeys && mappedTacticKeys.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-orange-800">
            {t('sms.analysis.tactics', 'Attack Tactics:')}
          </h4>
          <div className="mt-2 flex flex-wrap gap-2">
            {mappedTacticKeys.map((tacticKey) => (
              <span
                key={tacticKey}
                className="inline-flex items-center rounded-full bg-orange-200 px-3 py-1 text-xs font-medium text-orange-800"
              >
                {t(`sms.tactic.${tacticKey}`, tacticKey)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
